import Plugin from "@ckeditor/ckeditor5-core/src/plugin";
import ClickObserver from "@ckeditor/ckeditor5-engine/src/view/observer/clickobserver";
import ContextualBalloon from "@ckeditor/ckeditor5-ui/src/panel/balloon/contextualballoon";
import clickOutsideHandler from "@ckeditor/ckeditor5-ui/src/bindings/clickoutsidehandler";

import View from "@ckeditor/ckeditor5-ui/src/view";
import ViewCollection from "@ckeditor/ckeditor5-ui/src/viewcollection";
import InputTextView from "@ckeditor/ckeditor5-ui/src/inputtext/inputtextview";
import ButtonView from "@ckeditor/ckeditor5-ui/src/button/buttonview";

import KeystrokeHandler from "@ckeditor/ckeditor5-utils/src/keystrokehandler";
import FocusTracker from "@ckeditor/ckeditor5-utils/src/focustracker";
import FocusCycler, { FocusableView } from "@ckeditor/ckeditor5-ui/src/focuscycler";

import type { Editor } from "@ckeditor/ckeditor5-core";
import type { Locale } from "@ckeditor/ckeditor5-utils";
import type { Element, RootElement } from "@ckeditor/ckeditor5-engine";
import type Writer from "@ckeditor/ckeditor5-engine/src/model/writer";

import submitHandler from "@ckeditor/ckeditor5-ui/src/bindings/submithandler";

import "./poll.css";
import {
  PollProps,
  PollAnswer,
  MAX_POLL_ANSWERS,
  MAX_POLL_DURATION_DAYS,
  isMultipleChoicePoll,
} from "./constants";
import { randomId } from "../random";

export const DEFAULT_POLL_DURATION = { days: 7, hours: 0, minutes: 0 };

// Poll colour schemes (v2). Each is a light card background (`darkColor` ->
// --forum-event-background) with a saturated accent used for the text, slider
// line, bars and avatar outlines (`lightColor`/`bannerTextColor` ->
// --forum-event-foreground / --forum-event-banner-text). Order matches the
// colour picker in the design (neutral, orange, teal, blue, green).
export const POLL_COLOR_SCHEMES: PollProps["colorScheme"][] = [
  { darkColor: "#F5F5F5", lightColor: "#000000", bannerTextColor: "#000000" },
  { darkColor: "#FEF2EE", lightColor: "#D94300", bannerTextColor: "#D94300" },
  { darkColor: "#EDF6F7", lightColor: "#007584", bannerTextColor: "#007584" },
  { darkColor: "#EEF5F6", lightColor: "#004A83", bannerTextColor: "#004A83" },
  { darkColor: "#EEF6F0", lightColor: "#007311", bannerTextColor: "#007311" },
];

class MainFormView extends View {
  editor: Editor;
  selectedElement: Element | RootElement;
  questionView: InputTextView;
  agreeWordingView: InputTextView;
  disagreeWordingView: InputTextView;
  answersView: InputTextView;
  multiSelectView: View;
  colorSchemeButtons: ButtonView[];
  daysInputView: InputTextView;
  hoursInputView: InputTextView;
  minutesInputView: InputTextView;
  _focusables: ViewCollection<FocusableView>;
  focusTracker: FocusTracker;
  keystrokes: KeystrokeHandler;
  _focusCycler: FocusCycler;
  locale: Locale;

  constructor(locale: Locale, editor: Editor) {
    super(locale);

    this.editor = editor;
    this._createKeyAndFocusTrackers();

    this.selectedElement = null;

    const {
      questionView,
      agreeWordingView,
      disagreeWordingView,
      answersView,
      multiSelectView,
      colorSchemeButtons,
      daysInputView,
      hoursInputView,
      minutesInputView,
    } = this._createInputs();
    this.questionView = questionView;
    this.agreeWordingView = agreeWordingView;
    this.disagreeWordingView = disagreeWordingView;
    this.answersView = answersView;
    this.multiSelectView = multiSelectView;
    this.colorSchemeButtons = colorSchemeButtons;
    this.daysInputView = daysInputView;
    this.hoursInputView = hoursInputView;
    this.minutesInputView = minutesInputView;

    this.setTemplate({
      tag: "form",
      attributes: {
        class: ["ck", "ck-poll-form"],
        tabindex: "-1",
        spellcheck: "false",
      },
      children: [
        {
          tag: "div",
          attributes: {
            class: ["ck-poll-form-group"],
          },
          children: [
            {
              tag: "div",
              attributes: {
                // Label text is switched between "Statement" (slider) and
                // "Question" (multiple-choice) in `_syncMultipleChoiceFields`.
                class: ["ck-poll-form-label", "ck-poll-question-label"],
              },
              children: ["Statement"],
            },
            questionView,
          ],
        },
        {
          tag: "div",
          attributes: {
            class: ["ck-poll-form-row", "ck-poll-form-slider-group"],
          },
          children: [
            {
              tag: "div",
              attributes: {
                class: ["ck-poll-form-group"],
              },
              children: [
                {
                  tag: "div",
                  attributes: {
                    class: ["ck-poll-form-label"],
                  },
                  children: ["Left side"],
                },
                disagreeWordingView,
              ],
            },
            {
              tag: "div",
              attributes: {
                class: ["ck-poll-form-group"],
              },
              children: [
                {
                  tag: "div",
                  attributes: {
                    class: ["ck-poll-form-label"],
                  },
                  children: ["Right side"],
                },
                agreeWordingView,
              ],
            },
          ],
        },
        {
          tag: "div",
          attributes: {
            class: ["ck-poll-form-group", "ck-poll-form-mc-group"],
          },
          children: [
            {
              tag: "div",
              attributes: {
                class: ["ck-poll-form-label"],
              },
              children: [`Answers (one per line, up to ${MAX_POLL_ANSWERS})`],
            },
            answersView,
            multiSelectView,
          ],
        },
        {
          tag: "div",
          attributes: {
            class: ["ck-poll-form-row"],
          },
          children: [
            {
              tag: "div",
              attributes: {
                class: ["ck-poll-form-group"],
              },
              children: [
                {
                  tag: "div",
                  attributes: {
                    class: ["ck-poll-form-label", "ck-poll-form-label-bold"],
                  },
                  children: ["Color"],
                },
                {
                  tag: "div",
                  attributes: {
                    class: ["ck-poll-color-selector-container"],
                  },
                  children: this.colorSchemeButtons,
                },
              ],
            },
            {
              tag: "div",
              attributes: {
                class: ["ck-poll-form-group", "ck-poll-duration-group"],
              },
              children: [
                {
                  tag: "div",
                  attributes: {
                    class: ["ck-poll-form-label", "ck-poll-form-label-bold"],
                  },
                  children: ["Duration"],
                },
                {
                  tag: "div",
                  attributes: {
                    class: ["ck-poll-duration-input-container"],
                  },
                  children: [
                    this.daysInputView,
                    {
                      tag: "span",
                      attributes: { class: ["ck-poll-duration-label-unit"] },
                      children: ["days"],
                    },
                    this.hoursInputView,
                    {
                      tag: "span",
                      attributes: { class: ["ck-poll-duration-label-unit"] },
                      children: ["hours"],
                    },
                    this.minutesInputView,
                    {
                      tag: "span",
                      attributes: { class: ["ck-poll-duration-label-unit"] },
                      children: ["minutes"],
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    });
  }

  onClose() {
    this.questionView.element.classList.remove("ck-poll-error");
  }

  render() {
    super.render();

    submitHandler({
      view: this,
    });

    const childViews = [
      this.questionView,
      this.disagreeWordingView,
      this.agreeWordingView,
      this.answersView,
      // multiSelectView is a plain checkbox View without a focus() method, so
      // it's not part of the focus cycler; the native <input> is still tabbable.
      ...this.colorSchemeButtons,
      this.daysInputView,
      this.hoursInputView,
      this.minutesInputView,
    ];

    childViews.forEach((v) => {
      this._focusables.add(v);
      this.focusTracker.add(v.element);
    });

    this.keystrokes.listenTo(this.element);
  }

  focus() {
    this._focusCycler.focusFirst();
  }

  _createKeyAndFocusTrackers() {
    this.focusTracker = new FocusTracker();
    this.keystrokes = new KeystrokeHandler();
    this._focusables = new ViewCollection();

    this._focusCycler = new FocusCycler({
      focusables: this._focusables,
      focusTracker: this.focusTracker,
      keystrokeHandler: this.keystrokes,
      actions: {
        focusPrevious: "shift + tab",
        focusNext: "tab",
      },
    });
  }

  get question() {
    return this.questionView.element.value;
  }

  set question(value) {
    this.questionView.element.value = value;
  }

  _createInputs() {
    const questionView = new InputTextView(this.locale);
    const questionBind = questionView.bindTemplate;
    questionView.setTemplate({
      tag: "textarea",
      attributes: {
        type: "text",
        class: ["ck-poll-form-input", questionBind.if("hasError", "ck-error")],
        id: questionBind.to("id"),
        placeholder: questionBind.to("placeholder"),
        readonly: false,
        "aria-invalid": questionBind.if("hasError", true),
        "aria-describedby": questionBind.to("ariaDescribedById"),
      },
      on: {
        input: questionBind.to("input"),
      },
    });
    (questionView as AnyBecauseTodo).label = "Question";
    questionView.on("input", () => {
      const model = this.editor.model;
      const selectedElement = this.selectedElement;

      model.change((writer: Writer) => {
        if (!selectedElement) return;

        const props = this.selectedElement.getAttribute("props") as PollProps;
        const inputValue = questionView.element.value.trim();

        writer.setAttribute(
          "props",
          { ...props, question: inputValue },
          this.selectedElement,
        );
      });
    });

    // Create agree wording input
    const agreeWordingView = new InputTextView(this.locale);
    const agreeWordingBind = agreeWordingView.bindTemplate;
    agreeWordingView.setTemplate({
      tag: "input",
      attributes: {
        type: "text",
        class: ["ck-poll-form-input", agreeWordingBind.if("hasError", "ck-error")],
        id: agreeWordingBind.to("id"),
        readonly: false,
        "aria-invalid": agreeWordingBind.if("hasError", true),
        "aria-describedby": agreeWordingBind.to("ariaDescribedById"),
      },
      on: {
        input: agreeWordingBind.to("input"),
      },
    });
    (agreeWordingView as AnyBecauseTodo).label = "Right label";
    agreeWordingView.on("input", () => {
      const model = this.editor.model;
      const selectedElement = this.selectedElement;

      model.change((writer: Writer) => {
        if (!selectedElement) return;

        const props = this.selectedElement.getAttribute("props") as PollProps;
        const inputValue = agreeWordingView.element.value.trim();

        writer.setAttribute(
          "props",
          { ...props, agreeWording: inputValue },
          this.selectedElement,
        );
      });
    });

    // Create disagree wording input
    const disagreeWordingView = new InputTextView(this.locale);
    const disagreeWordingBind = disagreeWordingView.bindTemplate;
    disagreeWordingView.setTemplate({
      tag: "input",
      attributes: {
        type: "text",
        class: [
          "ck-poll-form-input",
          disagreeWordingBind.if("hasError", "ck-error"),
        ],
        id: disagreeWordingBind.to("id"),
        readonly: false,
        "aria-invalid": disagreeWordingBind.if("hasError", true),
        "aria-describedby": disagreeWordingBind.to("ariaDescribedById"),
      },
      on: {
        input: disagreeWordingBind.to("input"),
      },
    });
    (disagreeWordingView as AnyBecauseTodo).label = "Left label";
    disagreeWordingView.on("input", () => {
      const model = this.editor.model;
      const selectedElement = this.selectedElement;

      model.change((writer: Writer) => {
        if (!selectedElement) return;

        const props = this.selectedElement.getAttribute("props") as PollProps;
        const inputValue = disagreeWordingView.element.value.trim();

        writer.setAttribute(
          "props",
          { ...props, disagreeWording: inputValue },
          this.selectedElement,
        );
      });
    });

    // Create answers textarea (multiple-choice polls). One answer per line;
    // empty lines are ignored and the list is capped at MAX_POLL_ANSWERS.
    const answersView = new InputTextView(this.locale);
    const answersBind = answersView.bindTemplate;
    answersView.setTemplate({
      tag: "textarea",
      attributes: {
        type: "text",
        class: ["ck-poll-form-input", "ck-poll-answers-input"],
        id: answersBind.to("id"),
        rows: "5",
        readonly: false,
      },
      on: {
        input: answersBind.to("input"),
      },
    });
    (answersView as AnyBecauseTodo).label = "Answers";
    answersView.on("input", () => {
      const model = this.editor.model;
      const selectedElement = this.selectedElement;
      if (!selectedElement) return;

      model.change((writer: Writer) => {
        const props = selectedElement.getAttribute("props") as PollProps;
        // Preserve existing answer ids by position so edits to a published
        // poll's wording don't orphan votes that reference those ids.
        const previousAnswers = props.answers ?? [];
        const answers: PollAnswer[] = answersView.element.value
          .split("\n")
          .map((line) => line.trim())
          .filter((line) => line.length > 0)
          .slice(0, MAX_POLL_ANSWERS)
          .map((text, index) => ({
            _id: previousAnswers[index]?._id ?? randomId(),
            text,
          }));

        writer.setAttribute("props", { ...props, answers }, selectedElement);
      });
    });

    // Create the single/multi-select checkbox (multiple-choice polls)
    const multiSelectView = new View(this.locale);
    multiSelectView.setTemplate({
      tag: "label",
      attributes: { class: ["ck-poll-multiselect-toggle"] },
      children: [
        {
          tag: "input",
          attributes: {
            type: "checkbox",
            class: ["ck-poll-multiselect-checkbox"],
          },
          on: {
            change: multiSelectView.bindTemplate.to("change"),
          },
        },
        {
          tag: "span",
          children: ["Allow selecting multiple answers"],
        },
      ],
    });
    multiSelectView.on("change", () => {
      const selectedElement = this.selectedElement;
      if (!selectedElement) return;
      const checkbox = multiSelectView.element?.querySelector("input");
      const multiSelect = !!checkbox?.checked;
      this.editor.model.change((writer: Writer) => {
        const props = selectedElement.getAttribute("props") as PollProps;
        writer.setAttribute("props", { ...props, multiSelect }, selectedElement);
      });
    });

    // Create color scheme buttons
    const colorSchemeButtons = POLL_COLOR_SCHEMES.map((colorScheme, index) => {
      const buttonView = new ButtonView(this.locale);

      buttonView.on("render", () => {
        // Apply dynamic background color
        buttonView.element.style.backgroundColor = colorScheme.darkColor;
        // Add class on initial render
        buttonView.element.classList.add("ck-color-selector-button");
      });

      buttonView.on("execute", () => {
        const model = this.editor.model;
        const selectedElement = this.selectedElement;

        if (!selectedElement) return;

        model.change((writer: Writer) => {
          const props = this.selectedElement.getAttribute("props") as PollProps;
          const newColorScheme = POLL_COLOR_SCHEMES[index];

          writer.setAttribute(
            "props",
            { ...props, colorScheme: newColorScheme },
            this.selectedElement,
          );

          this.colorSchemeButtons.forEach((btn, btnIndex) => {
            btn.isOn = index === btnIndex;
            // Ensure the class is still present after potential CKEditor updates
            if (btn.element) {
              // Check if element exists
              btn.element.classList.add("ck-color-selector-button");
            }
          });
        });

        // Also ensure the class is on the clicked button itself after execute finishes
        if (buttonView.element) {
          buttonView.element.classList.add("ck-color-selector-button");
        }
      });

      return buttonView;
    });

    // --- Create Duration Inputs ---
    const createDurationInput = (
      label: string,
      initialValue: number = 0,
    ): InputTextView => {
      const inputView = new InputTextView(this.locale);
      const inputBind = inputView.bindTemplate;
      inputView.setTemplate({
        tag: "input",
        attributes: {
          type: "number",
          min: "0",
          class: ["ck-poll-form-input", "ck-duration-input"],
          id: inputBind.to("id"),
          "aria-label": label,
        },
        on: {
          input: inputBind.to("input"),
        },
      });
      (inputView as AnyBecauseTodo).label = label;

      // Add the input listener to update the model
      inputView.on("input", () => {
        const model = this.editor.model;
        const selectedElement = this.selectedElement;

        if (!selectedElement) return;

        model.change((writer: Writer) => {
          const props = selectedElement.getAttribute("props") as PollProps;

          // Read all duration values safely
          let days = parseInt(this.daysInputView.element.value, 10) || 0;
          let hours = parseInt(this.hoursInputView.element.value, 10) || 0;
          let minutes = parseInt(this.minutesInputView.element.value, 10) || 0;

          const totalMinutes = (days * 24 + hours) * 60 + minutes;
          if (totalMinutes > MAX_POLL_DURATION_DAYS * 24 * 60) {
            days = MAX_POLL_DURATION_DAYS;
            hours = 0;
            minutes = 0;
            this.daysInputView.element.value = days.toString();
            this.hoursInputView.element.value = hours.toString();
            this.minutesInputView.element.value = minutes.toString();
          }

          const newDuration = { days, hours, minutes };

          writer.setAttribute(
            "props",
            { ...props, duration: newDuration },
            selectedElement,
          );
        });
      });

      return inputView;
    };

    const daysInputView = createDurationInput("Days");
    const hoursInputView = createDurationInput("Hours");
    const minutesInputView = createDurationInput("Minutes");
    // --- End Duration Inputs ---

    return {
      questionView,
      agreeWordingView,
      disagreeWordingView,
      answersView,
      multiSelectView,
      colorSchemeButtons,
      daysInputView,
      hoursInputView,
      minutesInputView,
    };
  }
}

/**
 * Plugin for the form to edit contents of a poll
 */
export default class PollForm extends Plugin {
  formView: MainFormView;
  _balloon: ContextualBalloon;

  static get requires() {
    return [ContextualBalloon];
  }

  static get pluginName() {
    return "PollForm";
  }

  init() {
    const editor = this.editor;
    editor.editing.view.addObserver(ClickObserver);

    this.formView = this._createFormView();

    this._balloon = editor.plugins.get(ContextualBalloon);

    this._enableUserBalloonInteractions();
  }

  destroy() {
    super.destroy();

    this.formView.destroy();
  }

  _showUI(selectedElement: Element) {
    this._addFormView(selectedElement);

    this._balloon.showStack("main");
  }

  _createFormView() {
    const editor = this.editor;

    const formView = new MainFormView(editor.locale, editor);

    // Close plugin ui, if esc is pressed (while ui is focused)
    formView.keystrokes.set("esc", (_, cancel: () => void) => {
      this.formView.fire("submit");
      this._closeFormView();
    });

    // Cycle to the next input on enter, with a couple of exceptions...
    formView.keystrokes.set("Enter", (_, cancel: () => void) => {
      if (document.activeElement === formView.questionView.element) {
        // If the question view is focused, submit and close the form
        this.formView.fire("submit");
        this._closeFormView();
      } else if (document.activeElement !== formView.answersView.element) {
        // The answers view is a multi-line input so we need to be able to press
        // enter to add another answer - for anything else cycle to the next input
        this.formView._focusCycler.focusNext();
        cancel();
      }
    });

    return formView;
  }

  _addFormView(selectedElement: Element) {
    if (this._isFormInPanel) {
      // If the form is already open, update the values in case the selection changed
      // or if props were updated externally (less common but possible)
      this.formView.selectedElement = selectedElement;
      const pollProps = selectedElement.getAttribute("props") as PollProps;
      this.formView.questionView.element.value = pollProps.question || "";
      this.formView.agreeWordingView.element.value = pollProps.agreeWording || "";
      this.formView.disagreeWordingView.element.value =
        pollProps.disagreeWording || "";

      // Update duration inputs
      const duration = pollProps.duration || DEFAULT_POLL_DURATION; // Use default if missing
      this.formView.daysInputView.value = duration.days.toString();
      this.formView.hoursInputView.value = duration.hours.toString();
      this.formView.minutesInputView.value = duration.minutes.toString();

      // Update color button selection state
      const currentColorScheme = pollProps.colorScheme;
      const currentIndex = POLL_COLOR_SCHEMES.findIndex(
        (cs) =>
          cs.darkColor === currentColorScheme?.darkColor &&
          cs.lightColor === currentColorScheme?.lightColor,
      );
      this.formView.colorSchemeButtons.forEach((btn, btnIndex) => {
        btn.isOn = currentIndex === btnIndex;
      });

      this._syncMultipleChoiceFields(pollProps);

      return; // Don't add the view again
    }

    // --- If form is not already open ---
    this._balloon.add({
      view: this.formView,
      position: this._getBalloonPositionData(),
      balloonClassName: "ck-poll-balloon",
    });

    this.formView.questionView.select(); // Focus the first field
    this.formView.selectedElement = selectedElement;

    // Initialize values from the selected element's props
    const pollProps = selectedElement.getAttribute("props") as PollProps;
    this.formView.questionView.element.value = pollProps.question || "";
    this.formView.agreeWordingView.element.value = pollProps.agreeWording || "";
    this.formView.disagreeWordingView.element.value =
      pollProps.disagreeWording || "";

    // Initialize duration inputs
    const duration = pollProps.duration || DEFAULT_POLL_DURATION; // Use default if missing
    this.formView.daysInputView.value = duration.days.toString();
    this.formView.hoursInputView.value = duration.hours.toString();
    this.formView.minutesInputView.value = duration.minutes.toString();

    // Initialize color button selection state
    const currentColorScheme = pollProps.colorScheme;
    const currentIndex = POLL_COLOR_SCHEMES.findIndex(
      (cs) =>
        cs.darkColor === currentColorScheme?.darkColor &&
        cs.lightColor === currentColorScheme?.lightColor,
    );
    this.formView.colorSchemeButtons.forEach((btn, btnIndex) => {
      btn.isOn = currentIndex === btnIndex;
    });

    this._syncMultipleChoiceFields(pollProps);
  }

  /**
   * Reflect a poll's multiple-choice state into the form: toggle which fields
   * are shown (via a root class) and fill the answers textarea + multi-select
   * toggle.
   */
  _syncMultipleChoiceFields(pollProps: PollProps) {
    const isMc = isMultipleChoicePoll(pollProps);
    this.formView.element.classList.toggle("ck-poll-form--mc", isMc);
    const questionLabel = this.formView.element?.querySelector(
      ".ck-poll-question-label",
    );
    if (questionLabel) {
      // The slider is an agree/disagree statement; multiple-choice is a question.
      questionLabel.textContent = isMc ? "Question" : "Statement";
    }
    this.formView.answersView.element.value = (pollProps.answers ?? [])
      .map((answer) => answer.text)
      .join("\n");
    const checkbox = this.formView.multiSelectView.element?.querySelector("input");
    if (checkbox) {
      checkbox.checked = !!pollProps.multiSelect;
    }
  }

  _closeFormView() {
    if (!this._isFormInPanel) {
      return;
    }

    const editor = this.editor;

    this.stopListening(editor.ui, "update");
    this.stopListening(this._balloon, "change:visibleView");

    editor.editing.view.focus();

    if (this._isFormInPanel) {
      this._balloon.remove(this.formView);

      this.editor.editing.view.focus();
    }

    this.formView.onClose();
  }

  _getBalloonPositionData() {
    const view = this.editor.editing.view;
    const viewDocument = view.document;
    const target = view.domConverter.viewRangeToDom(
      viewDocument.selection.getFirstRange(),
    );
    return { target };
  }

  /**
   * Check if the current selected element is a poll and return it if so
   */
  _getSelectedPoll() {
    const selection = this.editor.model.document.selection;
    const selectedElement = selection.getSelectedElement();
    if (selectedElement && selectedElement.is("element", "poll")) {
      return selectedElement;
    }
    return null;
  }

  /**
   * Handle clicks to open and close the form view
   */
  _enableUserBalloonInteractions() {
    const viewDocument = this.editor.editing.view.document;

    // Show panel when a poll is clicked
    this.listenTo(viewDocument, "click", (evt, data) => {
      const selectedElement = this._getSelectedPoll();

      if (selectedElement) {
        this._showUI(selectedElement);
      }
    });

    // Close on click outside of balloon panel element
    clickOutsideHandler({
      emitter: this.formView,
      activator: () => true,
      contextElements: [this._balloon.view.element],
      callback: () => this._closeFormView(),
    });
  }

  get _isFormInPanel() {
    return this._balloon.hasView(this.formView);
  }
}
